import { useState } from 'react';
import useGlobalFilters from '../../../../hooks/useGlobalFilters';
import useTableMenu from '../../../../hooks/useTableMenu';
import {
    useDownloadVisitaActaMutation,
    useDownloadTechnicalVisitPdfMutation,
    useGetTiposVisitasQuery
} from '../../../../services/visitasApi';
import { VisitaEstado } from '../../../Visitas/consts/visitasStatus';
import { CLOSED_VISIT_STATE_IDS, getVisitsSrtStateIds, SRT_STATES } from '../../consts/states';
import { ALLOWED_VISIT_TYPE_IDS, ALLOWED_VISIT_TYPE_IDS_SET } from '../consts/visitTypes';
import { TEST_IDS } from '../../consts/testIds';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../../hooks/useTranslate';
import {
    useGenerateVisitFilesSRTMutation,
    useGetVisitsSRTQuery,
    useLazyExportVisitsSRTQuery
} from '../../../../services/rgrlVinculationApi';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../constants/values';
import { enqueueSnackbar } from 'notistack';
import { downloadBlobAsFile } from '../../../Visitas/utils/common';
import useDownloadFile from '../../../../hooks/useDownloadFile';
import { useLazyGetPresentacionRGRLPDFbyIdQuery } from '../../../../services/presentacionesApi';
import { getCurrentDate, getDateTextFieldFormat } from '../../../../utils/common';
import { useRoles } from '../../../../hooks/useRoles';
import { useOrganizationFilter } from '../../../../contexts/OrganizationFilterContext';
import { normalizeVisitsQueryParams } from '../utils/visitsQueryParams';
import { getVisitTypeId } from '../utils/visitTypes';
import {
    esErrorGenericoGeneracion,
    obtenerMensajeRechazoGeneracion
} from '../../utils/erroresGeneracion';

const TITULO_ERROR_GENERACION = 'No se pudo generar los archivos de visitas SRT';

const useFileGenerationVisits = (handleCloseDrawer: any) => {
    const { t } = useTranslation('filesGenerationSRT');
    
    const { t: visitsDownloadTranslations } = useTranslation('visitas');

    const visitsFilters = useGlobalFilters();
    const { filters, extraFilters } = visitsFilters;
    const { idEstadoSRT: selectedSrtState, idTipo: selectedVisitType, ...visitsRequestExtraFilters } = extraFilters || {};

    const selectedVisitTypeId = selectedVisitType?.id ?? selectedVisitType ?? null;
    const idsTipo = selectedVisitTypeId && ALLOWED_VISIT_TYPE_IDS_SET.has(selectedVisitTypeId)
        ? [selectedVisitTypeId]
        : ALLOWED_VISIT_TYPE_IDS;

    const { isDownloading, downloadFile } = useDownloadFile();
    const { translate } = useTranslate(t);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { data: visitTypes, isFetching: isFetchingVisitTypes } = useGetTiposVisitasQuery(null);
    const [downloadVisitaActa, { isLoading: isDownloadingVisitActa }] =
        useDownloadVisitaActaMutation();
    const [downloadTechnicalVisitPdf] = useDownloadTechnicalVisitPdfMutation();
    const [getPresentacionRGRLPDFbyId] = useLazyGetPresentacionRGRLPDFbyIdQuery();
    const [generateVisitsFiles, { isLoading: isGeneratingVisitsFiles }] =
        useGenerateVisitFilesSRTMutation();
    const [exportVisitsSRT, { isFetching: isExportingVisits }] = useLazyExportVisitsSRTQuery();

    const [selectedVisits, setSelectedVisits] = useState<any[]>([]);

    const [selected, setSelected] = useState<any | null>(null);
    const [selectedItemFromAction, setSelectedItemFromAction] = useState<any>(null);

    const visitsTableMenu = useTableMenu();
    const { isPerfilBackOffice } = useRoles();
    const { selectedOrganization } = useOrganizationFilter();

    const { handleOpenCard, anchorToEventElement, openMenu, closeMenu } = visitsTableMenu;

    const handleCloseMenu = () => {
        closeMenu();
    };

    const handleItemCheck = (selectedVisit: any) => {
        if (isPerfilBackOffice && !extraFilters?.idOrganizacion) {
            enqueueSnackbar(
                translate('drawers.generateFilesSRT.enqueues.warning.noOrganizationSelected'),
                {
                    variant: 'warning'
                }
            );
            return;
        }

        if (selectedVisit?.estadoSRT === SRT_STATES.ERROR) return;

        const updatedSectors = selectedVisits.some((visit) => visit.id === selectedVisit.id)
            ? selectedVisits.filter((visit) => visit.id !== selectedVisit.id)
            : [...selectedVisits, selectedVisit];

        setSelectedVisits(updatedSectors);
    };

    const handleRowClick = (selectedRowItem: any) => {
        setSelectedItemFromAction(selectedRowItem);
        setSelected(selectedRowItem);
        handleOpenCard();
    };

    const handleClickAction = (event: any, item: any, index: any) => {
        setSelectedItemFromAction(item);
        anchorToEventElement(event);
        openMenu();
    };

    const {
        data: visitas,
        refetch: refetchVisitas,
        isFetching: isFechingVisits
    } = useGetVisitsSRTQuery(normalizeVisitsQueryParams({
        ...filters,
        ...visitsRequestExtraFilters,
        idsEstado: 10, //PRESENTADO
        idsEstadoSRT: getVisitsSrtStateIds(selectedSrtState),
        idsTipo,
        ...(extraFilters?.idOrganizacion
            ? { idOrganizacion: extraFilters.idOrganizacion }
            : {})
    }));

    const visitTypesOptions = (visitTypes?.data || []).map((tipo: any) => ({
        id: tipo.id,
        nombre: tipo.descripcion,
        value: tipo.id
    }));

    const handleExportVisits = async () => {
        const { take, skip, ...restFilters } = filters || {};
        const normalizedParams = normalizeVisitsQueryParams({
            ...restFilters,
            ...visitsRequestExtraFilters,
            idsEstado: [10], //PRESENTADO
            idsEstadoSRT: getVisitsSrtStateIds(selectedSrtState),
            idsTipo,
            ...(extraFilters?.idOrganizacion
                ? { idOrganizacion: extraFilters.idOrganizacion }
                : {})
        });

        try {
            const response = await exportVisitsSRT(normalizedParams).unwrap();
            const presignedUrl =
                (response as any)?.data?.presignedUrl ?? (response as any)?.presignedUrl;
            if (!presignedUrl) throw new Error('La respuesta no trae presignedUrl');

            const todayDate = getCurrentDate();
            const formatDate = getDateTextFieldFormat(todayDate);
            const nombreArchivo =
                (response as any)?.data?.nombreArchivo ?? (response as any)?.nombreArchivo;
            const fileName = (nombreArchivo || `GestionSRT_Visitas_${formatDate}`).replace(
                /\.xlsx$/,
                ''
            );

            // fetch+blob, igual que el export de RGRL. Navegar a la presignedUrl con un <a>
            // no descargaba en prod cuando el export tardaba unos segundos: el bucket tiene
            // CORS para el dominio del Front, así que el fetch funciona en todos los ambientes.
            await downloadFile(presignedUrl, '.xlsx', fileName);
        } catch (error) {
            enqueueSnackbar(translate('alerts.export.error'), { variant: 'error' });
        }
    };

    const handleDownload = async ({
        tipo,
        requestFn,
        requestParams,
        filename,
        alertKeys
    }: {
        tipo: 'binary' | 'url';
        requestFn: any;
        requestParams: any;
        filename: string;
        alertKeys: {
            generating: string;
            success?: string;
            error: string;
            notFoundId?: string;
        };
    }) => {
        if (!selectedItemFromAction) return;

        if (tipo === 'url' && !selectedItemFromAction?.id) {
            dispatch(showAlert({ icon: 'error', message: t(alertKeys.notFoundId!) }));
            return;
        }

        try {
            dispatch(showAlert({ icon: 'info', message: t(alertKeys.generating), loading: true }));

            const response = await requestFn(requestParams).unwrap();

            if (tipo === 'binary' && response?.data) {
                downloadBlobAsFile(response.data, filename);
            } else if (tipo === 'url' && response?.data?.presignedUrls?.length > 0) {
                const url = response.data.presignedUrls[0];
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${filename}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }

            dispatch({ type: 'application/hideAlert' });
            if (alertKeys.success) enqueueSnackbar(t(alertKeys.success), { variant: 'success' });
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            let errorMessage = t(alertKeys.error);

            try {
                if (error?.data instanceof Blob) {
                    const text = await error.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                } else if (error?.error?.data?.message) {
                    errorMessage = error.error.data.message;
                } else if (error?.error) {
                    errorMessage = error.error;
                }
            } catch {}

            dispatch(showAlert({ icon: 'error', message: errorMessage }));
        }
    };

    const downloadVerosimilitud = () =>
        handleDownload({
            tipo: 'binary',
            requestFn: downloadVisitaActa,
            requestParams: {
                idVisita: selectedItemFromAction?.id,
                idTipoVisita: getVisitTypeId(selectedItemFromAction, selectedVisitType),
                tipoActa: 2
            },
            filename: 'ActaVerosimilitud',
            alertKeys: {
                generating: visitsDownloadTranslations('alerts.downloadVerosimilitud.generating'),
                error: visitsDownloadTranslations('alerts.downloadVerosimilitud.error')
            }
        });

    const downloadActa = () =>
        handleDownload({
            tipo: 'binary',
            requestFn: downloadVisitaActa,
            requestParams: {
                idVisita: selectedItemFromAction?.id,
                idTipoVisita: getVisitTypeId(selectedItemFromAction, selectedVisitType),
                tipoActa: 1
            },
            filename: visitsDownloadTranslations('table.actions.downloadActaFilename'),
            alertKeys: {
                generating: visitsDownloadTranslations('alerts.downloadActa.generating'),
                success: visitsDownloadTranslations('alerts.downloadActa.success') as string,
                error: visitsDownloadTranslations('alerts.downloadActa.error')
            }
        });

    const downloadRgrl = () =>
        handleDownload({
            tipo: 'url',
            requestFn: getPresentacionRGRLPDFbyId,
            requestParams: {
                idPresentacion: selectedItemFromAction?.id,
                typeId: 2
            },
            filename: 'PresentacionRGRL',
            alertKeys: {
                generating: visitsDownloadTranslations('alerts.downloadRgrl.generating'),
                error: visitsDownloadTranslations('alerts.downloadRgrl.error'),
                notFoundId: visitsDownloadTranslations('alerts.downloadRgrl.notFoundId') as string
            }
        });

    const downloadVisitaTecnica = async () => {
        if (!selectedItemFromAction) return;

        try {
            dispatch(
                showAlert({
                    icon: 'info',
                    message: visitsDownloadTranslations('alerts.downloadVisitaTecnica.generating'),
                    loading: true
                })
            );

            const response = await downloadTechnicalVisitPdf({
                idVisita: selectedItemFromAction?.id
            }).unwrap();

            const responseData =
                typeof response === 'string' ? response : (response as { data?: string })?.data;

            if (responseData) {
                const byteCharacters = atob(responseData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute(
                    'download',
                    `${visitsDownloadTranslations('table.actions.downloadVisitaTecnicaFilename')}.pdf`
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }

            dispatch({ type: 'application/hideAlert' });
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            let errorMessage = visitsDownloadTranslations('alerts.downloadVisitaTecnica.error');

            try {
                if (error?.data instanceof Blob) {
                    const text = await error.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                } else if (error?.error?.data?.message) {
                    errorMessage = error.error.data.message;
                } else if (error?.error) {
                    errorMessage = error.error;
                }
            } catch {}

            dispatch(showAlert({ icon: 'error', message: errorMessage }));
        }
    };

    const hasActividad = (visita: any, descripcion: 'VEROSIMILITUD' | 'RGRL' | 'VISITA TÉCNICA'): boolean =>
        visita?.actividades?.some(
            (a: any) => a?.tipoVisita?.descripcion?.toUpperCase() === descripcion
        ) ?? false;

    const ACTA_DOWNLOAD_ALLOWED_STATES = [VisitaEstado.PRESENTADO, VisitaEstado.REALIZADA];

    const generateVisitsTableMenuItems = (item: any) => {
        const isActaEstadoPermitido =
            item?.estado !== undefined &&
            (ACTA_DOWNLOAD_ALLOWED_STATES.includes(item.estado) ||
                (item.estado === VisitaEstado.CANCELADA &&
                    Boolean(item?.actaVerosimilitudDisponiblePorDatos)));

        const canDownloadActa = isActaEstadoPermitido;

        const canDownloadVerosimilitud = ACTA_DOWNLOAD_ALLOWED_STATES.includes(item?.estado)
            ? hasActividad(item, 'VEROSIMILITUD')
            : item?.estado === VisitaEstado.CANCELADA &&
              Boolean(item?.actaVerosimilitudDisponiblePorDatos) &&
              Boolean(item?.tieneVerosimilitud);

        const canDownloadVisitaTecnica = Boolean(item?.tieneVisitaTecnica) || hasActividad(item, 'VISITA TÉCNICA');

        return [
            {
                testID: TEST_IDS.table.visits.actions.seeVisit,
                onClick: () => {
                    handleCloseMenu();
                    navigate(`/backoffice/visitas/ejecucion/${item?.id}`);
                },
                text: translate('visits.table.actions.seeVisit')
            },
            ...(canDownloadActa
                ? [
                      {
                          testID: TEST_IDS.table.visits.actions.downloadVisitReport,
                          onClick: () => {
                              handleCloseMenu();
                              downloadActa();
                          },
                          text: visitsDownloadTranslations('table.actions.downloadActa')
                      }
                  ]
                : []),
            ...(canDownloadVerosimilitud
                ? [
                      {
                          onClick: () => {
                              handleCloseMenu();
                              downloadVerosimilitud();
                          },
                          text: visitsDownloadTranslations('table.actions.downloadVerosimilitud')
                      }
                  ]
                : []),
            ...(canDownloadVisitaTecnica
                ? [
                      {
                          onClick: () => {
                              handleCloseMenu();
                              downloadVisitaTecnica();
                          },
                          text: visitsDownloadTranslations('table.actions.downloadVisitaTecnica')
                      }
                  ]
                : [])
        ];
    };

    const generateAndDownloadFiles = async () => {
        try {
            if (selectedVisits?.length <= 0) {
                enqueueSnackbar(
                    translate('drawers.generateFilesSRT.enqueues.warning.noPresentationSelected'),
                    {
                        variant: 'warning'
                    }
                );
                return;
            }
            const visitsIds = selectedVisits.map((visit) => visit?.id);
            const adaptedBody = {
                idsVisita: visitsIds
            };
            const response: any = await generateVisitsFiles(adaptedBody);
            // Se chequea el error antes de leer `data`: si no, un rechazo del back
            // caía en un TypeError y terminaba en el aviso genérico de soporte.
            if ('error' in response) {
                // 400/409 con mensaje para el usuario (p. ej. la tanda mezcla
                // aseguradoras o la aseguradora no tiene código): se muestra tal cual.
                const mensajeRechazo = obtenerMensajeRechazoGeneracion(response.error);
                if (mensajeRechazo) {
                    dispatch(
                        showAlert({
                            title: TITULO_ERROR_GENERACION,
                            message: mensajeRechazo,
                            icon: 'error',
                            confirmText: translate('buttons.accept')
                        })
                    );
                    return;
                }
                if (esErrorGenericoGeneracion(response.error)) {
                    enqueueSnackbar(
                        translate('drawers.generateFilesSRT.enqueues.error.somethingWentWrong'),
                        {
                            variant: 'error'
                        }
                    );
                    return;
                }
                throw response.error;
            }

            const presignedUrl = response?.data?.data?.presignedUrl;
            if (presignedUrl) {
                const todayDate = getCurrentDate();
                const formatDate = getDateTextFieldFormat(todayDate);
                const fileName = `${t('drawers.generateFilesSRT.filename')}_${formatDate}`;
                await downloadFile(presignedUrl, '.zip', fileName);
            }

            enqueueSnackbar(translate('drawers.generateFilesSRT.enqueues.success.generated'), {
                variant: 'success'
            });
            setSelectedVisits([]);
            handleCloseDrawer();
        } catch (error) {
            dispatch(
                showAlert({
                    title: TITULO_ERROR_GENERACION,
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const visitsTableMenuItems = generateVisitsTableMenuItems(selectedItemFromAction);

    return {
        visitsFilters,
        visitas,
        isFechingVisits,
        visitTypes,
        isFetchingVisitTypes,
        visitTypesOptions,
        visitsTableMenu,
        selectedVisits,
        setSelectedVisits,
        selectedItemFromAction,
        selected,
        visitsTableMenuItems,
        isDownloading,
        isDownloadingVisitActa,
        isGeneratingVisitsFiles,
        generateAndDownloadFiles,
        handleRowClick,
        handleClickAction,
        handleItemCheck,
        handleExportVisits,
        isExportingVisits
    };
};

export default useFileGenerationVisits;
